import FacebookPixel from '@app-studio-web/ui/components/FacebookPixel'
import { AppConfigProvider } from '@app-studio-web/ui/contexts/AppConfigContext'
import ErrorPage from '@app-studio-web/ui/pages/ErrorPage'
import routes from '@pages/routes'
import * as Sentry from '@sentry/react'
import { newTracker } from '@snowplow/browser-tracker'
import { useEffect } from 'react'
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import appScreenshot from './assets/screenshot.webp'
import Logo from './components/Logo'

if (import.meta.env.MODE !== 'development') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN as string,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        createRoutesFromChildren,
        matchRoutes,
        useEffect: useEffect,
        useLocation,
        useNavigationType,
      }),
    ],
    tracesSampleRate: 0, // NOTE: Disable performance monitoring.
  })
}

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter)

const router = sentryCreateBrowserRouter(routes)

// Created here rather than from an effect. React flushes effects child-first,
// so a route that renders in the same commit as `App` fires its page view
// before `App`'s effect has run — and Snowplow drops calls made while no
// tracker is registered, taking the event with them. Routes whose loaders
// defer the first render happen to avoid that, which makes it a property of
// the route rather than of the tracking. Nothing here needs React, so it runs
// at import time and the ordering question disappears.
if (import.meta.env.MODE !== 'development') {
  newTracker('sp1', import.meta.env.VITE_SNOWPLOW_TRACKER_URL as string, {
    appId: import.meta.env.VITE_SNOWPLOW_APP_ID as string,
    platform: 'web',
  })
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorPage />}>
      <AppConfigProvider config={{ logo: <Logo />, appScreenshot }}>
        <FacebookPixel
          pixelId={import.meta.env.VITE_FACEBOOK_PIXEL_ID as string}
        />

        <RouterProvider router={router} />
      </AppConfigProvider>
    </Sentry.ErrorBoundary>
  )
}
